// Zod DTOs for the `/v1/media-assets` surface (P2-20,
// `docs/design/P2-20-video-hls-pipeline.md`). Three routes:
//
//   POST /v1/media-assets/:id/manifest-url   (§5, no request body)
//     -> { manifest, expiresIn }  — the REWRITTEN `.m3u8` TEXT (every bare
//        segment filename line replaced with a short-lived signed R2 GET
//        url), never a signed URL to the raw manifest object itself (see the
//        design doc §5 for why the manifest content must be mutated
//        per-request).
//
//   POST /v1/media-assets   (§7 point 3, ingest/registration)
//     { assetId, manifestFilename, segmentFilenames }
//     -> { mediaAssetId, manifest: {filename,url,headers,expiresIn},
//          segments: [{filename,url,headers,expiresIn}, ...] } — one
//        presigned PUT per file, `media/{mediaAssetId}/{filename}` (§3).
//
//   POST /v1/media-assets/:id/finalise   (§7 point 3, packaging verification)
//     {}  (deliberately EMPTY — see the schema's own comment below)
//     -> the finalised MediaAsset (READY or FAILED, never a 500 for "not
//        ready yet").
//
// Response shape for manifest-url mirrors what
// `apps/web/src/data/media/hlsManifest.ts` (already built/merged) is coded
// against verbatim — `{ manifest: string, expiresIn: number }` — this schema
// is the source of truth that file's TODO asks the backend route to confirm.
import { z } from 'zod';

// ─── POST /v1/media-assets/:id/manifest-url ─────────────────────────────────

export const mediaManifestUrlResponseSchema = z.object({
  /** The full `.m3u8` playlist text, with every segment reference line
   * already rewritten to a short-lived signed R2 GET url — safe for the
   * player to consume directly, no further server round-trip per segment. */
  manifest: z.string(),
  /** Always <= 300 (spec §7.2's signed-URL TTL ceiling, reused verbatim from
   * `R2_DOWNLOAD_PRESIGN_TTL_SECONDS`). Both the manifest fetch itself and
   * every segment URL embedded in it share this same TTL. */
  expiresIn: z.number().int().positive(),
});
export type MediaManifestUrlResponse = z.infer<typeof mediaManifestUrlResponseSchema>;

// ─── POST /v1/media-assets ───────────────────────────────────────────────
// The client already ran ffmpeg locally (design doc §2 option (a)) and knows
// exactly which manifest + segment filenames it is about to upload — this
// registers the `MediaAsset` row and mints one presigned PUT per file at the
// R2 key layout `media/{mediaAssetId}/{filename}` (§3).
//
// Filename SAFETY (charset allowlist, no traversal) is the security-critical
// check — it is re-validated AUTHORITATIVELY server-side in
// `MediaAssetsService.createPending` (via that module's own
// `SAFE_SEGMENT_FILENAME`/`isSafeSegmentFilename`, the single source of
// truth for "is this filename safe to key an R2 object with"), BEFORE any
// presigned URL is minted. This zod schema only enforces basic request
// SHAPE (non-empty, bounded length, bounded array size) so an obviously
// malformed request 400s before even reaching the service — it deliberately
// does not duplicate the charset regex, to avoid two copies of a
// security-relevant pattern drifting apart.
const mediaFilenameSchema = z.string().trim().min(1).max(255);

export const mediaAssetCreateRequestSchema = z.object({
  /** The source VIDEO `Asset.id` (P2-03 upload, already `READY`) this
   * packaging job is built from — verified server-side (kind=VIDEO,
   * status=READY) before any row is created. */
  assetId: z.string().min(1),
  manifestFilename: mediaFilenameSchema,
  /** At least one segment — an HLS VOD playlist with zero segments is not
   * playable. Upper bound (1000) is a security-review-set denial-of-wallet
   * ceiling (P2-20 security re-review, finding B-1) — a 2h lecture at 6s
   * segments is ~1200 segments, but this pipeline's real-world ingest fits
   * comfortably below 1000; a request above this cap 400s before minting any
   * presigned PUTs, writing an AuditLog row, or touching the idempotency KV
   * cache (all of which a 10,000-entry array could blow past). */
  segmentFilenames: z.array(mediaFilenameSchema).min(1).max(1000),
});
export type MediaAssetCreateRequest = z.infer<typeof mediaAssetCreateRequestSchema>;

const mediaAssetUploadTargetSchema = z.object({
  filename: z.string(),
  /** The presigned R2 PUT url — the client uploads THIS exact file's bytes
   * directly to `url` (never through the Worker). */
  url: z.string(),
  /** Headers the client MUST send with its direct `PUT` to `url` — mirrors
   * `AssetUploadUrlResponse.headers` (Content-Type is pinned into the SigV4
   * signature; a mismatched header 403s at R2). */
  headers: z.record(z.string(), z.string()),
  expiresIn: z.number().int().positive(),
});

export const mediaAssetCreateResponseSchema = z.object({
  mediaAssetId: z.string(),
  manifest: mediaAssetUploadTargetSchema,
  segments: z.array(mediaAssetUploadTargetSchema),
});
export type MediaAssetCreateResponse = z.infer<typeof mediaAssetCreateResponseSchema>;

// ─── POST /v1/media-assets/:id/finalise ─────────────────────────────────
// Deliberately EMPTY body — there is no `MediaAsset` schema column to
// persist the manifest/segment filenames the client originally declared at
// register time (no new migration this batch), so finalise does not ask the
// client to resend them either. Instead the server derives "what was
// actually uploaded" directly from the `BUCKET` binding's own object listing
// under `media/{mediaAssetId}/` — an untamperable, server-side source of
// truth — rather than trusting a client-resupplied file list a second time.
// See `MediaAssetsService.finalise`'s header for the full rationale.
export const mediaAssetFinaliseRequestSchema = z.object({}).strict();
export type MediaAssetFinaliseRequest = z.infer<typeof mediaAssetFinaliseRequestSchema>;

// ─── MediaAsset response (finalise's 200) ───────────────────────────────
const mediaAssetStatusSchema = z.enum(['PENDING', 'PROCESSING', 'READY', 'FAILED']);

export const mediaAssetResponseSchema = z.object({
  id: z.string(),
  assetId: z.string(),
  status: mediaAssetStatusSchema,
  /** Full R2 key of the HLS manifest — `null` until `READY`. */
  manifestKey: z.string().nullable(),
  durationSeconds: z.number().int().nullable(),
  failureReason: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type MediaAssetResponse = z.infer<typeof mediaAssetResponseSchema>;
